<script lang="ts">
  import { opdsStore } from '../stores/opds.svelte';
  import { PUBLIC_OPDS_CATALOGS } from '../data/opds-catalogs';
  import type { PublicOPDSCatalog } from '../data/opds-catalogs';
  import OPDSErrorAlert from './OPDSErrorAlert.svelte';

  let showAddCustom = $state(false);
  let customUrl = $state('');
  let customName = $state('');
  let customDescription = $state('');
  let addError = $state('');

  const { catalogs, error } = $derived(opdsStore);

  function isAdded(url: string): boolean {
    return catalogs.some((c) => c.url === url);
  }

  function addPublicCatalog(catalog: PublicOPDSCatalog) {
    opdsStore.addCatalog({
      name: catalog.name,
      url: catalog.url,
      description: catalog.description,
      language: catalog.language,
    });
  }

  function addCustomCatalog() {
    addError = '';

    if (!customUrl.trim()) {
      addError = 'URL is required';
      return;
    }

    if (!customName.trim()) {
      addError = 'Name is required';
      return;
    }

    // Validate URL format
    try {
      new URL(customUrl);
    } catch {
      addError = 'Invalid URL format';
      return;
    }

    opdsStore.addCatalog({
      name: customName.trim(),
      url: customUrl.trim(),
      description: customDescription.trim() || undefined,
    });

    // Reset form
    customUrl = '';
    customName = '';
    customDescription = '';
    showAddCustom = false;
  }

  function openCatalog(catalogId: string) {
    const catalog = catalogs.find((c) => c.id === catalogId);
    if (catalog) {
      opdsStore.openCatalog(catalog);
    }
  }

  function removeCatalog(catalogId: string, event: Event) {
    event.stopPropagation();
    if (confirm('Remove this catalog from your library?')) {
      opdsStore.removeCatalog(catalogId);
    }
  }
</script>

<div class="opds-catalog-list">
  <!-- Error Alert -->
  {#if error}
    <OPDSErrorAlert error={error} onDismiss={() => (opdsStore.error = null)} />
  {/if}

  <div class="section">
    <h2>My Catalogs</h2>

    {#if catalogs.length === 0}
      <p class="empty-state">
        No catalogs added yet. Add catalogs from the public list below or add your own.
      </p>
    {:else}
      <div class="catalog-grid">
        {#each catalogs as catalog (catalog.id)}
          <div class="catalog-card-wrapper">
            <button class="catalog-card" on:click={() => openCatalog(catalog.id)}>
              <div class="catalog-header">
                <h3>{catalog.name}</h3>
              </div>
              {#if catalog.description}
                <p class="catalog-description">{catalog.description}</p>
              {/if}
              {#if catalog.language}
                <span class="catalog-lang">{catalog.language.toUpperCase()}</span>
              {/if}
              {#if catalog.lastAccessed}
                <p class="catalog-meta">
                  Last accessed: {new Date(catalog.lastAccessed).toLocaleDateString()}
                </p>
              {/if}
            </button>
            <button
              class="btn-remove"
              on:click={(e) => removeCatalog(catalog.id, e)}
              aria-label="Remove catalog"
            >
              ×
            </button>
          </div>
        {/each}
      </div>
    {/if}
  </div>

  <div class="section">
    <div class="section-header">
      <h2>Add Catalog</h2>
      <button class="btn-secondary" on:click={() => (showAddCustom = !showAddCustom)}>
        {showAddCustom ? 'Cancel' : 'Add Custom URL'}
      </button>
    </div>

    {#if showAddCustom}
      <form class="add-custom-form" on:submit={(e) => { e.preventDefault(); addCustomCatalog(); }}>
        <div class="form-group">
          <label for="custom-name">Catalog Name *</label>
          <input
            id="custom-name"
            type="text"
            bind:value={customName}
            placeholder="My OPDS Catalog"
            required
          />
        </div>

        <div class="form-group">
          <label for="custom-url">Catalog URL *</label>
          <input
            id="custom-url"
            type="url"
            bind:value={customUrl}
            placeholder="https://example.com/opds/catalog"
            required
          />
        </div>

        <div class="form-group">
          <label for="custom-description">Description (optional)</label>
          <textarea
            id="custom-description"
            bind:value={customDescription}
            placeholder="Description of this catalog"
            rows="2"
          ></textarea>
        </div>

        {#if addError}
          <p class="error-message">{addError}</p>
        {/if}

        <button type="submit" class="btn-primary">Add Catalog</button>
      </form>
    {/if}
  </div>

  <div class="section">
    <h2>Public Catalogs</h2>
    <div class="catalog-grid">
      {#each PUBLIC_OPDS_CATALOGS as catalog (catalog.id)}
        {@const added = isAdded(catalog.url)}
        <div class="catalog-card" class:added>
          <div class="catalog-header">
            <h3>{catalog.name}</h3>
            <span class="catalog-lang">{catalog.language.toUpperCase()}</span>
          </div>
          <p class="catalog-description">{catalog.description}</p>
          <button
            class="btn-add"
            on:click={() => addPublicCatalog(catalog)}
            disabled={added}
          >
            {added ? '✓ Added' : '+ Add'}
          </button>
        </div>
      {/each}
    </div>
  </div>
</div>

<style>
  .opds-catalog-list {
    padding: 1rem;
  }

  .section {
    margin-bottom: 2rem;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #1f2937;
  }

  :global(.dark) h2 {
    color: #f9fafb;
  }

  .empty-state {
    color: #6b7280;
    font-style: italic;
    padding: 2rem;
    text-align: center;
    background: #f3f4f6;
    border-radius: 8px;
  }

  :global(.dark) .empty-state {
    color: #9ca3af;
    background: #1f2937;
  }

  .catalog-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
  }

  .catalog-card-wrapper {
    position: relative;
  }

  .catalog-card {
    padding: 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    transition: all 0.2s;
    text-align: left;
    width: 100%;
    cursor: pointer;
    color: #1f2937;
  }

  :global(.dark) .catalog-card {
    background: #1f2937;
    border-color: #374151;
    color: #f9fafb;
  }

  .catalog-card:hover {
    border-color: #3b82f6;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
  }

  :global(.dark) .catalog-card:hover {
    border-color: #60a5fa;
    box-shadow: 0 2px 8px rgba(96, 165, 250, 0.2);
  }

  .catalog-card.added {
    opacity: 0.6;
    cursor: default;
  }

  .catalog-card.added:hover {
    border-color: #e5e7eb;
    box-shadow: none;
  }

  :global(.dark) .catalog-card.added:hover {
    border-color: #374151;
  }

  .catalog-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.5rem;
  }

  .catalog-card h3 {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
    flex: 1;
    color: inherit;
  }

  .catalog-description {
    color: #6b7280;
    font-size: 0.9rem;
    margin: 0.5rem 0;
    line-height: 1.4;
  }

  :global(.dark) .catalog-description {
    color: #9ca3af;
  }

  .catalog-lang {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    background: #3b82f6;
    color: white;
    font-size: 0.75rem;
    font-weight: 600;
    border-radius: 4px;
    white-space: nowrap;
  }

  :global(.dark) .catalog-lang {
    background: #60a5fa;
    color: #111827;
  }

  .catalog-meta {
    color: #9ca3af;
    font-size: 0.85rem;
    margin-top: 0.5rem;
  }

  :global(.dark) .catalog-meta {
    color: #6b7280;
  }

  .btn-remove {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background: white;
    border: 1px solid #ef4444;
    color: #ef4444;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    width: 28px;
    height: 28px;
    line-height: 1;
    border-radius: 4px;
    z-index: 10;
  }

  :global(.dark) .btn-remove {
    background: #1f2937;
    border-color: #f87171;
    color: #f87171;
  }

  .btn-remove:hover {
    background: #ef4444;
    color: white;
  }

  :global(.dark) .btn-remove:hover {
    background: #f87171;
    color: #111827;
  }

  .btn-add {
    margin-top: 0.5rem;
    padding: 0.5rem 1rem;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    width: 100%;
  }

  .btn-add:hover:not(:disabled) {
    background: #2563eb;
  }

  .btn-add:disabled {
    background: #10b981;
    cursor: not-allowed;
  }

  :global(.dark) .btn-add:disabled {
    background: #059669;
  }

  .btn-primary,
  .btn-secondary {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
  }

  .btn-primary {
    background: #3b82f6;
    color: white;
  }

  .btn-primary:hover {
    background: #2563eb;
  }

  .btn-secondary {
    background: #6b7280;
    color: white;
  }

  .btn-secondary:hover {
    background: #4b5563;
  }

  :global(.dark) .btn-secondary {
    background: #4b5563;
  }

  :global(.dark) .btn-secondary:hover {
    background: #374151;
  }

  .add-custom-form {
    padding: 1rem;
    background: #f3f4f6;
    border-radius: 8px;
    margin-bottom: 1rem;
  }

  :global(.dark) .add-custom-form {
    background: #111827;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .form-group label {
    display: block;
    font-weight: 500;
    margin-bottom: 0.25rem;
    color: #374151;
  }

  :global(.dark) .form-group label {
    color: #d1d5db;
  }

  .form-group input,
  .form-group textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 1rem;
    background: white;
    color: #1f2937;
  }

  :global(.dark) .form-group input,
  :global(.dark) .form-group textarea {
    background: #1f2937;
    border-color: #4b5563;
    color: #f9fafb;
  }

  .form-group input:focus,
  .form-group textarea:focus {
    outline: none;
    border-color: #3b82f6;
    ring: 2px;
    ring-color: rgba(59, 130, 246, 0.3);
  }

  :global(.dark) .form-group input:focus,
  :global(.dark) .form-group textarea:focus {
    border-color: #60a5fa;
    ring-color: rgba(96, 165, 250, 0.3);
  }

  .error-message {
    color: #ef4444;
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }

  :global(.dark) .error-message {
    color: #f87171;
  }
</style>
